import React from 'react';
import { useAppStore, type Currency, type Country } from '../stores/appStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Settings, Globe, DollarSign, Calendar } from 'lucide-react';

interface GlobalSettingsProps {
  showHeader?: boolean;
}

export default function GlobalSettings({ showHeader = true }: GlobalSettingsProps) {
  const { currency, country, planningMonths, setCurrency, setCountry, setPlanningMonths } = useAppStore();

  const handlePlanningMonthsChange = (value: string) => {
    const months = parseInt(value);
    if (months >= 6 && months <= 60) {
      setPlanningMonths(months);
    }
  };

  return (
    <Card className="w-full">
      {showHeader && (
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Global Settings
          </CardTitle>
          <CardDescription>
            Configure global settings that affect the entire application, including exports and charts
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className="space-y-6">
        {/* Country Setting */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Country</Label>
          </div>
          <Select value={country} onValueChange={(value: Country) => setCountry(value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="India">🇮🇳 India</SelectItem>
              <SelectItem value="UAE">🇦🇪 UAE</SelectItem>
              <SelectItem value="USA">🇺🇸 USA</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Country setting affects regional defaults and formatting
          </p>
        </div>

        {/* Currency Setting */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Currency</Label>
          </div>
          <Select value={currency} onValueChange={(value: Currency) => setCurrency(value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="INR">INR (₹) - Indian Rupee</SelectItem>
              <SelectItem value="USD">USD ($) - US Dollar</SelectItem>
              <SelectItem value="AED">AED (د.إ) - UAE Dirham</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            All pricing, calculations, and exports will use this currency
          </p>
        </div>

        {/* Planning Period Setting */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Planning Period (Months)</Label>
          </div>
          <Input
            type="number"
            value={planningMonths}
            onChange={(e) => handlePlanningMonthsChange(e.target.value)}
            min={6}
            max={60}
            className="w-32"
          />
          <p className="text-xs text-muted-foreground">
            Number of months for financial projections (6-60 months)
          </p>
        </div>

        {/* Settings Summary */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <h4 className="text-sm font-medium mb-2">Current Settings Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Country:</span>
              <span className="ml-2 font-medium">{country}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Currency:</span>
              <span className="ml-2 font-medium">{currency}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Planning Period:</span>
              <span className="ml-2 font-medium">{planningMonths} months</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}